import { initializeBoard } from './chessLogic';

// Create a custom board setup for tutorials
const createTutorialBoard = (setup) => {
  const board = Array(8).fill(null).map(() => Array(8).fill(null));
  
  setup.forEach(({ piece, position }) => {
    const [row, col] = position;
    board[row][col] = piece;
  });
  
  return board;
};

export const getTutorialLessons = () => [
  {
    title: "Basic Piece Movement",
    description: "Learn how each chess piece moves",
    steps: [
      {
        title: "Moving a Pawn",
        instruction: "Pawns move forward one square. From their starting position, they can move two squares. Click on the white pawn in front of the king, then click two squares forward.",
        type: "move",
        board: initializeBoard(),
        expectedMove: {
          from: [6, 4], // e2
          to: [4, 4]    // e4
        },
        hint: "Click on the pawn at e2 (in front of the king), then click on e4 (two squares forward)."
      },
      {
        title: "Moving a Knight",
        instruction: "Knights move in an L-shape: two squares in one direction and one square perpendicular. Move the knight from b1 to c3.",
        type: "move",
        board: initializeBoard(),
        expectedMove: {
          from: [7, 1], // b1
          to: [5, 2]    // c3
        },
        hint: "Click on the knight at b1, then click on c3. Knights are the only pieces that can jump over other pieces."
      },
      {
        title: "Moving a Bishop",
        instruction: "Bishops move diagonally any number of squares. First, move a pawn to make way, then move the bishop.",
        type: "explanation",
        board: createTutorialBoard([
          { piece: { type: 'king', color: 'white' }, position: [7, 4] },
          { piece: { type: 'bishop', color: 'white' }, position: [7, 5] },
          { piece: { type: 'pawn', color: 'white' }, position: [5, 6] },
          { piece: { type: 'king', color: 'black' }, position: [0, 4] }
        ]),
        hint: "Bishops can only move diagonally. They stay on the same color squares throughout the game."
      },
      {
        title: "Moving a Rook",
        instruction: "Rooks move horizontally or vertically any number of squares. Move the rook from a1 to a4.",
        type: "move",
        board: createTutorialBoard([
          { piece: { type: 'king', color: 'white' }, position: [7, 4] },
          { piece: { type: 'rook', color: 'white' }, position: [7, 0] },
          { piece: { type: 'king', color: 'black' }, position: [0, 4] }
        ]),
        expectedMove: {
          from: [7, 0], // a1
          to: [4, 0]    // a4
        },
        hint: "Click on the rook at a1, then click on a4. Rooks move in straight lines."
      },
      {
        title: "Moving the Queen",
        instruction: "The Queen is the most powerful piece. She can move like a rook or bishop - any direction, any distance.",
        type: "explanation",
        board: createTutorialBoard([
          { piece: { type: 'king', color: 'white' }, position: [7, 4] },
          { piece: { type: 'queen', color: 'white' }, position: [7, 3] },
          { piece: { type: 'king', color: 'black' }, position: [0, 4] }
        ]),
        hint: "The Queen combines the power of a rook and bishop. She's your most valuable piece after the king."
      },
      {
        title: "Moving the King",
        instruction: "The King moves one square in any direction. Move the king from e1 to f1.",
        type: "move",
        board: createTutorialBoard([
          { piece: { type: 'king', color: 'white' }, position: [7, 4] },
          { piece: { type: 'king', color: 'black' }, position: [0, 4] }
        ]),
        expectedMove: {
          from: [7, 4], // e1
          to: [7, 5]    // f1
        },
        hint: "Click on the king, then click on an adjacent square. The king is the most important piece!"
      }
    ]
  },
  {
    title: "Capturing Pieces",
    description: "Learn how to capture opponent pieces",
    steps: [
      {
        title: "Pawn Captures",
        instruction: "Pawns capture diagonally forward. Capture the black pawn with your white pawn.",
        type: "move",
        board: createTutorialBoard([
          { piece: { type: 'king', color: 'white' }, position: [7, 4] },
          { piece: { type: 'pawn', color: 'white' }, position: [4, 4] },
          { piece: { type: 'pawn', color: 'black' }, position: [3, 5] },
          { piece: { type: 'king', color: 'black' }, position: [0, 4] }
        ]),
        expectedMove: {
          from: [4, 4], // e4
          to: [3, 5]    // f5
        },
        hint: "Pawns capture differently than they move. Click the white pawn, then click on the black pawn diagonally."
      },
      {
        title: "Knight Captures",
        instruction: "Knights capture the same way they move - in an L-shape. Capture the black piece with your knight.",
        type: "move",
        board: createTutorialBoard([
          { piece: { type: 'king', color: 'white' }, position: [7, 4] },
          { piece: { type: 'knight', color: 'white' }, position: [5, 2] },
          { piece: { type: 'pawn', color: 'black' }, position: [3, 3] },
          { piece: { type: 'king', color: 'black' }, position: [0, 4] }
        ]),
        expectedMove: {
          from: [5, 2], // c3
          to: [3, 3]    // d5
        },
        hint: "Move your knight in an L-shape to capture the black pawn."
      },
      {
        title: "Understanding Check",
        instruction: "When the king is attacked, it's called 'check'. The king must move to safety, block the attack, or capture the attacking piece.",
        type: "explanation",
        board: createTutorialBoard([
          { piece: { type: 'king', color: 'white' }, position: [7, 4] },
          { piece: { type: 'rook', color: 'black' }, position: [6, 4] },
          { piece: { type: 'king', color: 'black' }, position: [0, 4] }
        ]),
        hint: "The white king is in check from the black rook. In a real game, you must get out of check immediately."
      }
    ]
  },
  {
    title: "Special Rules",
    description: "Learn about special chess rules",
    steps: [
      {
        title: "Castling",
        instruction: "Castling is a special move involving the king and rook. You can castle when neither piece has moved, there are no pieces between them, and the king is not in check.",
        type: "explanation",
        board: createTutorialBoard([
          { piece: { type: 'king', color: 'white' }, position: [7, 4] },
          { piece: { type: 'rook', color: 'white' }, position: [7, 7] },
          { piece: { type: 'king', color: 'black' }, position: [0, 4] }
        ]),
        hint: "Castling helps protect your king and develop your rook. It's the only move where you can move two pieces at once."
      },
      {
        title: "En Passant",
        instruction: "En passant is a special pawn capture. If an opponent's pawn moves two squares and lands next to your pawn, you can capture it as if it only moved one square.",
        type: "explanation",
        board: createTutorialBoard([
          { piece: { type: 'king', color: 'white' }, position: [7, 4] },
          { piece: { type: 'pawn', color: 'white' }, position: [3, 4] },
          { piece: { type: 'pawn', color: 'black' }, position: [3, 5] },
          { piece: { type: 'king', color: 'black' }, position: [0, 4] }
        ]),
        hint: "En passant can only be played immediately after the opponent's pawn moves two squares."
      },
      {
        title: "Pawn Promotion",
        instruction: "When a pawn reaches the opposite end of the board, it must be promoted to any piece except a king (usually a queen).",
        type: "explanation",
        board: createTutorialBoard([
          { piece: { type: 'king', color: 'white' }, position: [7, 4] },
          { piece: { type: 'pawn', color: 'white' }, position: [1, 6] },
          { piece: { type: 'king', color: 'black' }, position: [0, 4] }
        ]),
        hint: "Pawn promotion is very powerful. Most players choose a queen because it's the strongest piece."
      }
    ]
  },
  {
    title: "Winning the Game",
    description: "Learn about checkmate and other ways the game can end",
    steps: [
      {
        title: "Checkmate",
        instruction: "Checkmate occurs when the king is in check and cannot escape. This is how you win the game!",
        type: "explanation",
        board: createTutorialBoard([
          { piece: { type: 'king', color: 'white' }, position: [7, 4] },
          { piece: { type: 'queen', color: 'white' }, position: [1, 4] },
          { piece: { type: 'rook', color: 'white' }, position: [0, 7] },
          { piece: { type: 'king', color: 'black' }, position: [0, 4] }
        ]),
        hint: "The black king is in checkmate - it's in check and has no legal moves to escape."
      },
      {
        title: "Stalemate",
        instruction: "Stalemate occurs when a player has no legal moves but is not in check. This results in a draw.",
        type: "explanation",
        board: createTutorialBoard([
          { piece: { type: 'king', color: 'white' }, position: [6, 5] },
          { piece: { type: 'queen', color: 'white' }, position: [5, 6] },
          { piece: { type: 'king', color: 'black' }, position: [7, 7] }
        ]),
        hint: "The black king is not in check but has no legal moves. This is stalemate - a draw."
      },
      {
        title: "Basic Strategy",
        instruction: "Control the center, develop your pieces quickly, and keep your king safe. These are fundamental chess principles.",
        type: "explanation",
        board: createTutorialBoard([
          { piece: { type: 'king', color: 'white' }, position: [7, 6] },
          { piece: { type: 'rook', color: 'white' }, position: [7, 5] },
          { piece: { type: 'knight', color: 'white' }, position: [5, 2] },
          { piece: { type: 'bishop', color: 'white' }, position: [4, 3] },
          { piece: { type: 'pawn', color: 'white' }, position: [4, 4] },
          { piece: { type: 'king', color: 'black' }, position: [0, 4] }
        ]),
        hint: "Good development: pieces are active, king is castled to safety, and central pawns control important squares."
      },
      {
        title: "Congratulations!",
        instruction: "You've completed the chess tutorial! You now know the basic rules and can start playing. Practice makes perfect!",
        type: "explanation",
        board: initializeBoard(),
        hint: "Keep practicing and studying chess to improve your game. Have fun playing!"
      }
    ]
  }
];